import { useCallback, useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useInvalidateFilesQueries } from '@/api/hooks/useInvalidateFileQueries';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { WorkspaceObjectType } from '@/enums/';
import { useAppSelector } from '@/store';
import type { WorkspaceState } from '@/store/interfaces';
import type { WorkspaceObjectTreeItem } from '@/types/workspace';
import type {
  MoveObjectContextValue,
  MoveObjectWorkspaceContextValue,
  WorkspaceInfo,
} from './Context';
import {
  MoveObjectContext,
  MoveObjectWorkspaceContext,
} from './Context';
import { useMoveObjectMutation } from './hooks/useMoveObjectMutation';

type Props =
  {
    enabled?: boolean;
    objectType: WorkspaceObjectType;
  } &
  IWorkspaceObjectId &
  IWorkspaceId &
  ChildrenProps;

export const MoveObjectContainer = ({
  enabled = true,
  ...props
}: Props) => {

  const personalWorkspace = useAppSelector(state => state.workspace.personal);
  const groupWorkspace = useAppSelector(state => state.workspace.group);
  const workspaceBrandsEnabled = useAppSelector(state => state.features.workspaceBrands);
  const [workspace, setWorkspace] = useState<WorkspaceInfo>();
  const [parentObjectId, setParentObjectId] = useState<number>(null);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: ['get/object:move-details', props.objectId], queryFn: () => {
      return apiClient.workspacesObject.moveObjectDetails({
        objectId: props.objectId,
        workspaceId: props.workspaceId,
      }).then(res => ({
        ...res,
        items: res.items.filter(f => workspaceBrandsEnabled ? true : f.typeId !== WorkspaceObjectType.Brand),
      }));
    },
    enabled,
  });

  useEffect(() => {
    const workspaceId = query.data?.object?.workspaceId;
    if (workspaceId) {
      setWorkspace(workspaceId === personalWorkspace.entity.id ? buildWorkspaceInfo(personalWorkspace) : buildWorkspaceInfo(groupWorkspace));
    }
  }, [groupWorkspace, personalWorkspace, query.data?.object?.workspaceId]);

  const invalidateQueries = useInvalidateFilesQueries();

  const moveObjectMutation = useMoveObjectMutation(props.objectType);

  useEffect(() => {
    setParentObjectId(null);
  }, [workspace?.id]);

  const onSave = useCallback(() => {
    return moveObjectMutation.mutateAsync({
      objectId: props.objectId,
      parentObjectId,
      workspaceId: query.data.object.workspaceId,
    }).then(() => {
      invalidateQueries();
    });
  }, [
    invalidateQueries,
    moveObjectMutation,
    props.objectId,
    query.data?.object?.workspaceId,
    parentObjectId,
  ]);

  const currentPath = useMemo(() => {
    if (!query.data?.breadcrumbs) return [];
    return query.data.breadcrumbs.map(m => m.name);
  }, [query.data?.breadcrumbs]);

  const buildTreeItem = useCallback((objectId: number): WorkspaceObjectTreeItem => {
    const objects = query.data?.items ?? [];
    const object = objects.find(f => f.id === objectId);

    const children = Object.values(objects)
      .filter(f => f.parentObjectId === objectId)
      .map(m => buildTreeItem(m.id));

    const sortedChildren = [...children].sort((a, b) => a.object.name.localeCompare(b.object.name));

    return {
      children: sortedChildren,
      object,
    };
  }, [query.data?.items]);

  const tree = useMemo(() => {
    if (!workspace?.objectId) return null;
    return buildTreeItem(workspace?.objectId);
  }, [
    buildTreeItem,
    workspace?.objectId,
  ]);

  const loading = query.isInitialLoading || !tree;

  const value: MoveObjectContextValue = {
    currentPath,
    data: tree,
    object: query.data?.object,
    loadingTree: loading,
    isSaving: moveObjectMutation.isPending,
    onSave,
    onSelectParentObject: setParentObjectId,
    parentObjectId,
  };

  const workspaceValue: MoveObjectWorkspaceContextValue = {
    loadingWorkspaces: loading,
    onSelectWorkspace: setWorkspace,
    selectedWorkspace: workspace,
    workspaces: [buildWorkspaceInfo(personalWorkspace), buildWorkspaceInfo(groupWorkspace)],
  };

  return (
    <MoveObjectWorkspaceContext.Provider value={workspaceValue}>
      <MoveObjectContext.Provider value={value}>
        {props.children}
      </MoveObjectContext.Provider>
    </MoveObjectWorkspaceContext.Provider>
  );
};

function buildWorkspaceInfo(workspace: WorkspaceState['personal'] | WorkspaceState['group']): WorkspaceInfo {
  return {
    id: workspace.entity.id,
    name: workspace.object.name,
    objectId: workspace.object.id,
  };
}

export default MoveObjectContainer;